import { useParams, useNavigate } from 'react-router-dom';
import { FiSave } from 'react-icons/fi';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import SelectTextField from '../../molecules/SelectTextField';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Loading from '../../ions/Loading';
import VerificationConfigSkeleton from './VerificationConfigSkeleton';
import { COLLATERAL_BREADCRUMBS, formatCurrency, formatCpfCnpj, formatDate, getIntensityColor } from './collateralUtils';
import VerificationConfigStatus from './VerificationConfigStatus';
import ConfirmationStatus from './ConfirmationStatus';
import useVerificationConfigPageEdit from '../../../hooks/useVerificationConfigPageEdit';

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-3 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

const Field = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex justify-between items-center py-0.5">
        <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
        {color ? (
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${color}`}>{value}</span>
        ) : (
            <span className="text-xs dark:text-white text-right max-w-[60%] truncate" title={String(value ?? '')}>{value != null && value !== '' ? value : '—'}</span>
        )}
    </div>
);

const Checkbox = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-center gap-2 cursor-pointer py-1">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
        <span className="text-xs dark:text-white">{label}</span>
    </label>
);

const STATUS_LABELS: Record<string, string> = {
    BAIXA: 'BAIXA', MEDIA: 'MÉDIA', ALTA: 'ALTA', CAOTICA: 'CAÓTICA',
};
const t = (v: string) => STATUS_LABELS[v] || v;

const VerificationConfigPageEdit = () => {
    const {
        id, navigate, isLoading, form, set, ev,
        analystSearch, setAnalystSearch, analystResults, setAnalystResults, searchAnalyst,
        handleSave,
    } = useVerificationConfigPageEdit();

    if (isLoading) return <VerificationConfigSkeleton />;

    return (
        <PageForm
            pageTitle={`Editar — ${form.operationCode || ''}`}
            apiVersion={form.apiVersion ? `${form.apiName} - ${form.apiVersion} (${(form.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.verificationConfig,
                { label: form.operationCode, url: `/collateral/verification-config/${id}/view` },
                { label: 'Editar', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end items-center space-x-2">
                    <button onClick={() => navigate(-1)}
                        className="px-2.5 rounded text-xs flex items-center h-[30px] bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 dark:hover:bg-slate-600">
                        Cancelar
                    </button>
                    <button onClick={handleSave}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]">
                        <FiSave size={13} /> <span>Salvar</span>
                    </button>
                </div>
            }
        >
            <div className="mt-4 space-y-4">
                {/* Row 1: Obrigação + Configuração (read-only) */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <SectionTitle color="text-blue-700 dark:text-blue-400">Obrigação</SectionTitle>
                        <div className="space-y-0.5">
                            <Field label="Tipo operação" value={form.operationType} />
                            <Field label="Obrigação" value={form.obligation || '—'} />
                            <Field label="Cedente" value={form.assignor} />
                            <Field label="CNPJ Cedente" value={formatCpfCnpj(form.assignorTaxId)} />
                            <Field label="Nº de títulos" value={form.totalTitles} />
                            <Field label="Valor total" value={formatCurrency(form.totalAmount || 0)} />
                            <Field label="Data emissão" value={formatDate(form.issueDate)} />
                        </div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                        <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                        <div className="space-y-0.5">
                            <Field label="Trader" value={form.traderName} />
                            <Field label="Intensidade" value={t(form.analysisIntensity)} color={form.analysisIntensity ? getIntensityColor(form.analysisIntensity) : undefined} />
                            <Field label="Critério análise" value={form.analysisCriteria || '—'} />
                            {form.sampledTitles && <Field label="Títulos amostragem" value={`${form.sampledTitles} (${form.totalTitles > 0 ? Math.round(form.sampledTitles / form.totalTitles * 100) : 0}%)`} />}
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-gray-500 dark:text-gray-400 text-xs">Status garantia</span>
                                <VerificationConfigStatus status={form.guaranteeStatus} />
                            </div>
                            <div className="flex justify-between items-center py-0.5">
                                <span className="text-gray-500 dark:text-gray-400 text-xs">Status validação</span>
                                <ConfirmationStatus status={form.validationStatus} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: Análise (editável) */}
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <SectionTitle color="text-amber-700 dark:text-amber-400">Análise</SectionTitle>
                    <div className="space-y-4">
                        <div className="relative">
                            <TextEdit label="Responsável (Enter para buscar)" value={analystSearch || form.analystName || ''}
                                onChange={e => setAnalystSearch(e.target.value)}
                                onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); searchAnalyst(analystSearch); } }} />
                            {analystResults.length > 0 && (
                                <div className="absolute z-50 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-lg max-h-32 overflow-y-auto">
                                    {analystResults.map((p: any) => (
                                        <button key={p.id} onClick={() => { set('analystName', p.name); setAnalystSearch(''); setAnalystResults([]); }}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white">
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Pendência</span>
                            <ReactQuill value={form.pendency || ''} onChange={(v: string) => set('pendency', v)} theme="snow" className="mt-1 bg-white dark:bg-slate-700" />
                        </div>
                        <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Observação</span>
                            <ReactQuill value={form.observation || ''} onChange={(v: string) => set('observation', v)} theme="snow" className="mt-1 bg-white dark:bg-slate-700" />
                        </div>
                    </div>
                </div>

                {/* Row 3: Controles booleanos (editável) */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                        <Checkbox label="Lastro salvo em garantias" checked={!!form.collateralSaved} onChange={v => set('collateralSaved', v)} />
                    </div>
                    <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded space-y-2">
                        <Checkbox label="Registro em cartório" checked={!!form.courtRegistration} onChange={v => set('courtRegistration', v)} />
                        {form.courtRegistration && (
                            <SelectTextField label="Qual contrato registrar?" value={form.contractToRegister || ''} items={[{ value: 'CF DE RECEBÍVEIS', label: 'CF de Recebíveis' }, { value: 'CF DA CONTA ESCROW', label: 'CF da Conta Escrow' }, { value: 'ADITAMENTO', label: 'Aditamento' }]} onChange={ev('contractToRegister')} />
                        )}
                    </div>
                    {form.debtorNotificationDone != null && (
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded">
                            <Checkbox label="Cedente notificou os sacados?" checked={!!form.debtorNotificationDone} onChange={v => set('debtorNotificationDone', v)} />
                        </div>
                    )}
                </div>
            </div>
        </PageForm>
    );
};

export default VerificationConfigPageEdit;
